import { doc, getDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DailyMIT } from '@/types';
import { ensureDayExists } from './days.repository';

// ============================================
// Collection Path Helpers
// ============================================

// MITs are stored in the day document: users/{uid}/days/{date}.mits[]
const getDayRef = (userId: string, date: string) => doc(db, 'users', userId, 'days', date);

// ============================================
// Helper Functions
// ============================================

export const generateMITId = (): string => {
  return `mit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// ============================================
// CRUD Operations
// ============================================

export async function getMITs(userId: string, date: string): Promise<DailyMIT[]> {
  const dayRef = getDayRef(userId, date);
  const snapshot = await getDoc(dayRef);

  if (!snapshot.exists()) {
    return [];
  }

  const data = snapshot.data();
  const mits: DailyMIT[] = data?.mits || [];

  // Sort by order
  return [...mits].sort((a, b) => a.order - b.order);
}

export async function updateMITs(userId: string, date: string, mits: DailyMIT[]): Promise<void> {
  // Ensure day document exists
  await ensureDayExists(userId, date);

  const dayRef = getDayRef(userId, date);
  await updateDoc(dayRef, {
    mits,
    updatedAt: serverTimestamp(),
  });
}

export async function addMIT(
  userId: string,
  date: string,
  mit: Omit<DailyMIT, 'id'>
): Promise<DailyMIT> {
  const currentMits = await getMITs(userId, date);

  const newMIT: DailyMIT = {
    id: generateMITId(),
    ...mit,
  };

  const updatedMits = [...currentMits, newMIT].sort((a, b) => a.order - b.order);
  await updateMITs(userId, date, updatedMits);

  return newMIT;
}

export async function updateMIT(
  userId: string,
  date: string,
  mitId: string,
  updates: Partial<DailyMIT>
): Promise<void> {
  const currentMits = await getMITs(userId, date);
  const mitIndex = currentMits.findIndex((m) => m.id === mitId);

  if (mitIndex === -1) {
    throw new Error('MIT not found');
  }

  const updatedMits = [...currentMits];
  updatedMits[mitIndex] = { ...updatedMits[mitIndex], ...updates };

  await updateMITs(userId, date, updatedMits);
}

export async function toggleMITCompletion(
  userId: string,
  date: string,
  mitId: string
): Promise<void> {
  const currentMits = await getMITs(userId, date);
  const mit = currentMits.find((m) => m.id === mitId);

  if (!mit) {
    throw new Error('MIT not found');
  }

  await updateMIT(userId, date, mitId, { completed: !mit.completed });
}

export async function removeMIT(userId: string, date: string, mitId: string): Promise<void> {
  const currentMits = await getMITs(userId, date);
  const updatedMits = currentMits.filter((m) => m.id !== mitId);

  await updateMITs(userId, date, updatedMits);
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToMITs(
  userId: string,
  date: string,
  onData: (mits: DailyMIT[]) => void,
  onError?: (error: Error) => void
): () => void {
  const dayRef = getDayRef(userId, date);

  return onSnapshot(
    dayRef,
    (snapshot) => {
      const dayData = snapshot.data();
      const mitsData: DailyMIT[] = dayData?.mits || [];

      // Sort by order
      const sortedMits = [...mitsData].sort((a, b) => a.order - b.order);
      onData(sortedMits);
    },
    (error) => {
      console.error('Error listening to daily MITs:', error);
      onError?.(error);
    }
  );
}
